
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Loader2, Bot, User, MessageSquare } from 'lucide-react';
import { getConciergeResponse } from '../services/geminiService';
import { Order, MenuItem, Reservation, AppSettings } from '../types';

interface ConciergeProps {
  orders: Order[];
  menu: MenuItem[];
  reservations: Reservation[];
  settings: AppSettings;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export const Concierge: React.FC<ConciergeProps> = ({ orders, menu, reservations, settings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'こんにちは！SpaceSimpleのマネージャーアシスタントです。売上分析やメニューの提案、予約の確認など、何でもお手伝いしますよ。🌿' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsLoading(true);

    try {
      const response = await getConciergeResponse(userMsg, { orders, menu, reservations, settings });
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'すみません、エラーが起きてしまいました。時間をおいて再度お試しください。' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-stone-800 text-white rounded-full shadow-2xl z-50 flex items-center justify-center group border-2 border-white/20"
      >
        <Sparkles size={24} className="group-hover:rotate-12 transition-transform" />
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-400 rounded-full animate-ping opacity-75" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-end justify-end p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.9, x: 20 }}
              animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
              exit={{ opacity: 0, y: 50, scale: 0.9, x: 20 }}
              className="w-full max-w-md bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-stone-100 overflow-hidden flex flex-col h-[70vh] pointer-events-auto"
            >
              {/* Header */}
              <div className="bg-stone-800 p-6 text-white flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-400 rounded-2xl flex items-center justify-center shadow-lg">
                    <Sparkles size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-black text-sm tracking-tight">AI Manager</h3>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-[10px] font-bold opacity-60">Online / Connected to Gemini</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>

              {/* Chat Body */}
              <div 
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar bg-stone-50/50"
              >
                {messages.map((msg, idx) => (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-3 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${msg.role === 'user' ? 'bg-stone-200 text-stone-600' : 'bg-orange-100 text-orange-600'}`}>
                        {msg.role === 'user' ? <User size={16} /> : <Bot size={16} />}
                      </div>
                      <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user' 
                          ? 'bg-stone-800 text-white rounded-tr-none' 
                          : 'bg-white text-stone-700 shadow-sm border border-stone-100 rounded-tl-none whitespace-pre-wrap'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex gap-3 max-w-[85%]">
                      <div className="w-8 h-8 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center shadow-sm">
                        <Loader2 size={16} className="animate-spin" />
                      </div>
                      <div className="px-4 py-3 rounded-2xl bg-white text-stone-400 shadow-sm border border-stone-100 rounded-tl-none italic text-xs">
                        データを分析しています...
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Suggestions */}
              {!isLoading && messages.length === 1 && (
                <div className="px-6 py-2 flex gap-2 overflow-x-auto no-scrollbar bg-stone-50/50">
                  {["今日の売上分析をして", "人気メニューTOP3", "明日の予約は？"].map(s => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); handleSend(); }}
                      className="px-3 py-1.5 bg-white border border-stone-200 rounded-full text-[10px] font-bold text-stone-500 hover:border-orange-200 hover:text-orange-600 whitespace-nowrap transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <form onSubmit={handleSend} className="p-4 bg-white border-t border-stone-100 flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="メッセージを入力..."
                  className="flex-1 bg-stone-50 border-none rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-stone-200 outline-none"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  className="w-12 h-12 bg-stone-800 text-white rounded-2xl flex items-center justify-center hover:bg-stone-700 transition-colors disabled:opacity-30 shadow-lg shadow-stone-900/10"
                >
                  <Send size={18} />
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
